import { useAuthenticated } from "@gettactic/hooks";
import { UserPropertyType } from "@gettactic/api";
import { WeeklyDataLayout } from "@gettactic/components/src/components/WeeklyOffice/WeeklyDataLayout";
import { format } from "date-fns";
import { WeeklyTable } from "./TeamSchedule/WeeklyTable";
import { ChartConfig } from "@gettactic/components/src/@v2/components/ui/chart";
import { InOfficeChartLegend } from "@gettactic/components/src/components/WeeklyOffice/legend/InOfficeChartLegend";
import { WeeklyChart } from "./TeamSchedule/WeeklyChart";
import { TeamAttendanceHeader } from "./TeamSchedule/TeamAttendanceHeader";
import { TeamAttendanceFilters } from "./TeamSchedule/TeamAttendanceFilters";
import { useTeamAttendanceFilters } from "./TeamSchedule/hooks/useTeamAttendanceFilters";
import { api } from "@/lib/api/api";
import { SelectUsers } from "./SelectUsers";
import { useMemo } from "react";
import { ReservationModals } from "./reservation-modals/ReservationModals";
import { SelectInput } from "@cmp-src/form/SelectInput";
import { SelectItem } from "@v2/components/ui/select";
import pluralize from "pluralize";

const chartConfig: ChartConfig = {
  inOffice: {
    label: "In office",
    color: "hsl(var(--chart-1))",
    indicator: "dot"
  },
  favorite: {
    color: "hsl(var(--chart-2))",
    label: "Favorite",
    indicator: "circle"
  }
};

const TeamSchedule = ({
  daysCount,
  showAsMobileDaily,
  selectedDayIndex,
  showHeader = true
}: {
  daysCount: number;
  showAsMobileDaily?: boolean;
  selectedDayIndex?: number;
  showHeader?: boolean;
}) => {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();

  const {
    filters: dashboardFilters,
    isComponentLoading,
    totalUsers,
    tableOverviewData,
    chartData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    setUsersFilter,
    setTeams,
    handleSaveFilterData
  } = useTeamAttendanceFilters({
    authenticatedUser,
    currentOffice,
    selectedDayIndex
  });

  const { formattedWeek, somethingWentWrong } = useMemo(() => {
    try {
      const formatted =
        dashboardFilters.week?.map((date) => format(date, "yyyy-MM-dd")) || [];
      return { formattedWeek: formatted, somethingWentWrong: false };
    } catch (e) {
      return { formattedWeek: [], somethingWentWrong: true };
    }
  }, [dashboardFilters.week]);

  if (somethingWentWrong) {
    return (
      <div className="flex justify-center p-4">
        Unable to process date information
      </div>
    );
  }

  const weeklyTable = (
    <WeeklyTable
      daysCount={daysCount}
      userSearch={
        !showAsMobileDaily ? (
          <SelectUsers
            className={"hidden md:block"}
            // disabled={isComponentLoading}
            onChange={(ids: string[]) => {
              handleSaveFilterData(UserPropertyType.LastUsersFilter, ids);
              setUsersFilter(ids);
            }}
            values={dashboardFilters.users}
          />
        ) : undefined
      }
      authenticatedUser={authenticatedUser}
      data={tableOverviewData}
      formattedWeek={formattedWeek}
      favsApi={api}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isLoading={isLoading}
      showAsMobileDaily={showAsMobileDaily}
      selectedDayIndex={selectedDayIndex}
    />
  );

  if (showAsMobileDaily)
    return (
      <>
        {showHeader && (
          <div className={"flex items-center gap-2 pl-2"}>
            <div className={"flex-1 text-[15px] text-neutral-1000"}>
              <strong>{totalUsers}</strong> {pluralize("people", totalUsers)} in office today{" "}
            </div>
            <div>
              <TeamAttendanceFilters
                teams={dashboardFilters.teams}
                onTeamsChange={setTeams}
                onSaveFilterData={handleSaveFilterData}
                isComponentLoading={isComponentLoading}
                triggerClassName={
                  "py-3 px-3.5 rounded-md border-1 border-neutral-100"
                }
              />
            </div>
          </div>
        )}
        <div className={"rounded-lg border-1 border-neutral-200 bg-white px-4"}>
          {weeklyTable}
        </div>
        <ReservationModals />
      </>
    );

  return (
    <div className="mt-4 mb-12 flex flex-row justify-center md:mt-16">
      <div className="flex w-full flex-col space-y-4">
        {showHeader && (
          <div className="flex flex-col items-end justify-between md:flex-row">
            <TeamAttendanceHeader
              authenticatedUserName={authenticatedUser.user?.name}
              totalUsers={totalUsers}
            />
            <TeamAttendanceFilters
              teams={dashboardFilters.teams}
              onTeamsChange={setTeams}
              onSaveFilterData={handleSaveFilterData}
              isComponentLoading={isComponentLoading}
            />
          </div>
        )}
        <div>
          <WeeklyDataLayout
            chart={
              <WeeklyChart
                daysCount={daysCount}
                isLoading={isLoading}
                chartInfo={{
                  chartData: chartData,
                  chartConfig: chartConfig
                }}
                myId={authenticatedUser.user?.id || ""}
                userCount={totalUsers}
              />
            }
            legend={<InOfficeChartLegend chartConfig={chartConfig} />}
            table={weeklyTable}
            daysCount={daysCount}
          />
        </div>
      </div>
      <ReservationModals />
    </div>
  );
};

export default TeamSchedule;
